// Card #1652 (design §6.1/§6.2 types 6/7) — the ranked rows shared by
// Mid-Class Result and End of Class Result. score-api's composeRankedList
// (lib/scoreboard-mode-payload.js) already drops omitted/placeholder rows
// BEFORE assigning `rank = index + 1` over the surviving set, which is what
// AC3's "omitting an entry never shifts or renumbers the ranks around it"
// actually depends on. This component must not re-derive its own number
// from array position (that would silently reintroduce the exact
// renumbering bug the server-side ordering already avoids) — it always
// displays the row's own `rank`/`placing` value verbatim.
//
// `rankMode` picks the wording (design §4.5): "rank" (Mid-Class Result,
// type 6 — NOT end-of-class yet) vs "placing" (End of Class Result, type 7
// — the one case §4.5 permits "place"/"placing" language).
//
// Card #1780 — this renders as a real TABLE with a header row, and the
// number cell carries a BARE number: the word "Rank"/"Place" appears once
// in the heading, never repeated on every row.
//
// Why this does NOT delegate to <EntryIdentity>: that component is an
// inline run of sibling spans (one selectable field, design §6.1), which
// cannot column-align. A table needs each identity sub-key in its own
// cell. The sub-key class names and testids below are deliberately kept
// identical to EntryIdentity's so the component vocabulary stays one
// vocabulary — only the layout differs.
//
// Card #1944 — SCORE follows the SAME conditional-column rule the identity
// sub-keys already follow (#1780 AC5, computeIdentityColumns above): the
// column renders only when AT LEAST ONE row in the class has a score, and
// vanishes entirely (not a permanently-blank column) when none do — a class
// with zero scored rides doesn't grow a dead Score column no ride will ever
// fill. Once the column IS showing, AC4 still governs individual rows: a
// row with no score of its own gets an empty cell, no column collapse, no
// shift of any other column. These two rules don't conflict — AC4 only ever
// speaks to a row lacking a score, never to the all-rows-empty case, which
// is #1780's rule instead. Formatting goes through score-common's
// formatPercentage (3 decimals, same formatter ScoreHero/PerJudgeBreakout
// already use) — score-api's composeRankedList carries the stored composite
// through verbatim, this component only decides how to print it.
import { formatPercentage } from "score-common";
import "./RankedList.css";

// The identity sub-keys composeEntryIdentity can emit, in display order.
// `present` mirrors that composer's own per-key emission rule: a CNO of 0
// would still be a CNO, the three name/status strings are meaningful only
// when non-empty.
const IDENTITY_COLUMNS = [
  {
    key: "competitorNumber",
    heading: "CNO",
    testid: "entry-identity-cno",
    className: "entry-identity__cno",
    present: (v) => v != null,
    render: (v) => `#${v}`,
  },
  {
    key: "riderName",
    heading: "Rider",
    testid: "entry-identity-rider",
    className: "entry-identity__rider",
    present: (v) => !!v,
    render: (v) => v,
  },
  {
    key: "riderStatus",
    heading: "Status",
    testid: "entry-identity-status",
    className: "entry-identity__status",
    present: (v) => !!v,
    render: (v) => v,
  },
  {
    key: "horseName",
    heading: "Horse",
    testid: "entry-identity-horse",
    className: "entry-identity__horse",
    present: (v) => !!v,
    render: (v) => v,
  },
];

// AC5 — a sub-key NO row resolved gets no column at all (the #1652 AC3
// "absent, never rendered empty" rule, applied at column scope). A
// sub-key SOME rows resolved keeps its column, and the rows that lack it
// render an empty cell, so no other column shifts.
//
// Exported (card #1791) so a caller splitting one class's rows across
// several side-by-side tables (RankedListLayout, groups of 10) can compute
// this ONCE against the full class and pass the same `columns` into every
// table. Computing it per-table off each table's own slice would let two
// halves disagree on which sub-keys are present (e.g. a scratched rider's
// `riderStatus` landing only in rows 11-20), which would misalign the two
// tables' headings against each other for no reason tied to the data.
export function computeIdentityColumns(rankedList) {
  if (!Array.isArray(rankedList)) return [];
  return IDENTITY_COLUMNS.filter((column) =>
    rankedList.some((row) => column.present(row?.entryIdentity?.[column.key]))
  );
}

// Card #1944 — same #1780 AC5 rule as computeIdentityColumns, applied to the
// Score column: present only when at least one row in the FULL class has a
// score. Exported for the same reason computeIdentityColumns is (#1791) — a
// caller splitting one class across several side-by-side tables
// (RankedListLayout) must compute this once against the full list and pass
// the same answer into every table, or two halves could disagree on whether
// Score shows at all.
// Card #2060 — a terminated ride carries `termReason` (its specific DR123.3
// outcome, score-api's composeRankedList) instead of, or alongside, a
// numeric score, and it renders in this SAME column (see the row map below).
// A class where every row is a term reason with no numeric score anywhere
// must still show the column, or AC2's "instead of a bare score" would never
// render for it.
export function computeShowScoreColumn(rankedList) {
  if (!Array.isArray(rankedList)) return false;
  return rankedList.some((row) => row?.score != null || row?.termReason);
}

export default function RankedList({
  rankedList,
  rankMode = "rank",
  columns: columnsProp,
  showScore: showScoreProp,
}) {
  if (!Array.isArray(rankedList) || !rankedList.length) return null;
  const label = rankMode === "placing" ? "Place" : "Rank";
  const columns = columnsProp || computeIdentityColumns(rankedList);
  const showScore = showScoreProp != null ? showScoreProp : computeShowScoreColumn(rankedList);

  return (
    <table className="ranked-list" data-testid="ranked-list">
      <thead>
        <tr className="ranked-list__head-row" data-testid="ranked-list-head-row">
          <th scope="col" className="ranked-list__number" data-testid="ranked-list-number-heading">
            {label}
          </th>
          {columns.map((column) => (
            <th
              key={column.key}
              scope="col"
              className={column.className}
              data-testid={`ranked-list-heading-${column.key}`}
            >
              {column.heading}
            </th>
          ))}
          {showScore && (
            <th scope="col" className="ranked-list__score" data-testid="ranked-list-score-heading">
              Score
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {rankedList.map((row, index) => {
          const number = rankMode === "placing" ? row.placing : row.rank;
          // AC4 — two separate reasons there may be no number to show, and
          // neither may be papered over:
          //   * `placing` is undefined until an admin sets score-ride.placing
          //     (composeRankedList, scoreboard-mode-payload.js).
          //   * `placing` 0 means UNPLACED. Jaybill, 2026-08-20: "unplaced ==
          //     0, it has to be that way for FVD." A 0 reaching a public
          //     audience screen as "Place 0" is the bug; it is NOT a
          //     zero-indexed array leaking, and it must NEVER be fixed by
          //     adding 1 — `rank` is already 1-based, and the rides carrying
          //     0 are scratched/unplaced, so +1 would promote every one of
          //     them to "1st". See docs/score/fvd-data-model.md.
          // Never falls back to `row.rank` or `index + 1` here either, that
          // would renumber against the server order.
          const hasNumber = typeof number === "number" && Number.isFinite(number) && number > 0;
          return (
            <tr key={index} className="ranked-list__row" data-testid="ranked-list-row">
              <td className="ranked-list__number" data-testid="ranked-list-number-cell">
                {hasNumber ? <span data-testid="ranked-list-number">{number}</span> : null}
              </td>
              {columns.map((column) => {
                const value = row?.entryIdentity?.[column.key];
                return (
                  <td key={column.key} className={column.className}>
                    {column.present(value) ? (
                      <span data-testid={column.testid}>{column.render(value)}</span>
                    ) : null}
                  </td>
                );
              })}
              {showScore && (
                <td className="ranked-list__score">
                  {row.termReason ? (
                    // Card #2060 AC2/AC3 — a terminated ride (e.g. a No
                    // Show reaching FINAL with composite 0, #2058's judge
                    // finding) shows its specific outcome here INSTEAD of
                    // the score, so a 0 is never a bare, unexplained
                    // number. `row.termReason` is already one of the
                    // DR123.3 labels (Scratched/Excused/Eliminated/No
                    // Show, docs/score/term-reason-vocabulary.md) composed
                    // server-side — rendered verbatim, no re-derivation.
                    <span className="ranked-list__term-reason" data-testid="ranked-list-term-reason">
                      {row.termReason}
                    </span>
                  ) : row.score != null ? (
                    <span data-testid="ranked-list-score">{formatPercentage(Number(row.score))}</span>
                  ) : null}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
